/**
 * Payment-link flow constants — ported verbatim from bridge
 * (navigo-module/components/payFromBridge/paymentLinkConstants.js).
 *
 * The numbers are wire/state values shared with bridge's behaviour; do not
 * renumber them to "tidy up".
 */

/** Screens in the payment dialog. -1 is a local sentinel for "deciding". */
export const SCREEN_STATE = {
  SELECT_PAYMENT_METHOD: 0,
  SELECT_SHARE_LINK_OPTIONS: 1,
  SUCCESS_WITH_GENERATED_LINK: 2,
  SUCCESS_TOTAL_PAYMENT_WITH_WALLET: 3,
  ERROR_CREATING_APPOINTMENT: 4,
  APPOINTMENT_CREATED_BUT_LINK_NOT_GENERATED: 5,
};

/** Nothing valid to select — bridge's setPaymentMethod(-1). */
export const NO_VALID_PAYMENT_METHOD = -1;

export const PAYMENT_METHOD = {
  WALLET_AND_RAZORPAY: 1,
  RAZORPAY_ONLY: 2,
  WALLET_ONLY: 3,
  /** Rendered, but disabled: the wallet exists and is empty. */
  WALLET_DISABLED: 4,
};

/**
 * Which payment methods the sublimit response permits, for a NEW appointment.
 *
 * Bridge's SelectPaymentMethod.validOptions, create branch only
 * (withCreateAppointment === true). phr is create-only, so bridge's
 * existing-appointment branch — which has a missing-return bug and can fall
 * through to undefined — is deliberately not ported.
 *
 * Returns [] rather than undefined when nothing matches; callers must handle an
 * empty list by disabling Continue rather than rendering an empty radio group.
 */
export const validPaymentOptions = (sublimit) => {
  if (!sublimit) return [];

  const grandTotal = sublimit.grandTotal || 0;
  const useZylaWallet = sublimit.useZylaWallet;
  const available = (sublimit.zylaWallet && sublimit.zylaWallet.available) || 0;

  if (!sublimit.isWalletIncluded) return [PAYMENT_METHOD.RAZORPAY_ONLY];

  if (sublimit.isUnlimitedWallet && useZylaWallet === grandTotal) {
    return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_ONLY];
  }
  // eslint-disable-next-line eqeqeq
  if (useZylaWallet == grandTotal) {
    // Loose == is bridge's, and load-bearing: these arrive as float64 and a
    // string/number mismatch here would drop the wallet option entirely.
    return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_ONLY];
  }
  if (useZylaWallet > 0 && useZylaWallet < grandTotal) {
    return [PAYMENT_METHOD.WALLET_AND_RAZORPAY, PAYMENT_METHOD.RAZORPAY_ONLY];
  }
  if (available <= 0 && useZylaWallet <= 0) {
    return [PAYMENT_METHOD.RAZORPAY_ONLY, PAYMENT_METHOD.WALLET_DISABLED];
  }

  return [PAYMENT_METHOD.RAZORPAY_ONLY];
};

/**
 * Razorpay cannot charge under Rs 1, so an option whose Razorpay leg rounds
 * below that is unselectable. Bridge's isWalletAndRazorpayTooSmall /
 * isRazorpayOnlyTooSmall.
 */
export const isOptionTooSmall = (option, sublimit) => {
  if (!sublimit) return false;
  const useExternalWallet = sublimit.useExternalWallet;
  const grandTotal = sublimit.grandTotal || 0;

  if (option === PAYMENT_METHOD.WALLET_AND_RAZORPAY) {
    return useExternalWallet > 0 && useExternalWallet < 1;
  }
  if (option === PAYMENT_METHOD.RAZORPAY_ONLY) {
    return grandTotal > 0 && grandTotal < 1;
  }

  return false;
};
